/**
 * Multi-select for related KB articles.
 * Max 6, excludes current article.
 */

import React from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import type { KBArticle } from '@municipality/types';

function getTitle(article: KBArticle): string {
  const tr = article.translations;
  const primary = tr[article.languageSettings.primaryLanguage]?.title;
  if (primary) return primary;
  const en = tr.en?.title;
  if (en) return en;
  const first = Object.values(tr)[0]?.title;
  return first ?? article.id;
}

export interface RelatedArticlesSelectorProps {
  articles: KBArticle[];
  value: string[];
  onChange: (ids: string[]) => void;
  excludeArticleId?: string;
  maxCount?: number;
}

export const RelatedArticlesSelector: React.FC<RelatedArticlesSelectorProps> = ({
  articles,
  value,
  onChange,
  excludeArticleId,
  maxCount = 6,
}) => {
  const { t } = useTranslation(['knowledgeBase']);
  const options = articles
    .filter((a) => a.id !== excludeArticleId)
    .map((a) => ({ label: getTitle(a), value: a.id }));

  const handleChange = (ids: string[]): void => {
    onChange(ids.slice(0, maxCount));
  };

  return (
    <Select
      mode="multiple"
      placeholder={t('articles.relatedArticles')}
      value={value}
      onChange={handleChange}
      options={options}
      style={{ width: '100%' }}
      maxTagCount="responsive"
      filterOption={(input, opt) =>
        (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
    />
  );
};
